import { Ionicons, MaterialIcons } from "@expo/vector-icons";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import _ from "lodash";
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Input,
  Pressable,
  ScrollView,
  Spinner,
  Text,
  useTheme,
  View,
  VStack,
} from "native-base";
import React, { useEffect, useState } from "react";
import { TFunction, useTranslation } from "react-i18next";
import { useWindowDimensions } from "react-native";
import { NavigationState, Route, SceneRendererProps, TabBar } from "react-native-tab-view";
import { useDispatch, useSelector } from "react-redux";

import { CommonNumberInputWithPlusMinusButtons, CommonStepIndex } from "../commons";
import { MemoizedIngredientsRoute } from "../components/IngredientsRoute";
import PreparationStepsRoute from "../components/PreparationStepsRoute";
import MacroProgressWidget from "../components/RecipeMacrosItem";
import {
  commonStyles,
  Images,
  IS_MOBILE_PLATFORM,
  isDesktopScreen,
  Routes,
  Scale,
  VerticalScale,
  width,
} from "../constants";
import { MACRO_NAMES } from "../constants/data";
import { HORMONE_HEX_COLOR } from "../constants/theme";
import { getPlannerData } from "../helpers/diaryHelpers";
import { FeatureFlag, isFeatureFlagActive } from "../helpers/featureFlags";
import {
  createIngredientDescriptionComponent,
  getServingDescriptionText,
  isInstagramExternalSource,
} from "../helpers/foodHelpers";
import {
  formatMealType,
  formatNumberAsDecimal,
  formatNumberAsWholeNumber,
  getRecipeMealImage,
  openURL,
} from "../helpers/generalHelpers";
import { getDislikedRecipe, getFavouritedRecipe } from "../helpers/userHelpers";
import { useExternalLink } from "../hooks/useExternalLink";
import type { RootStackParamList } from "../navigation/NavigationStackParams";
import backendApi from "../services/backendApi";
import type {
  Food,
  GeneratedRecipeMeal,
  GeneratedRecipeMealIngredient,
  Ingredient,
  IngredientPostRequest,
  RecipeMeal,
  SuggestedServing,
  UserFavouriteRecipe,
} from "../services/backendTypes";
import logger from "../services/logger";
import {
  userDislikeRecipesSelector,
  userFavouriteRecipesSelector,
  userSelector,
  userSlice,
  viewAsUserSelector,
} from "../slices/userSlice";
import type { MacroName, RecipeMacrosItemType } from "../types";
import styles from "./RecipeDetailScreenStyle";

const {
  useUsersUserfavouriterecipeCreateMutation,
  useUsersUserdislikerecipeCreateMutation,
  useUsersUserfavouriterecipeDestroyMutation,
  useUsersUserdislikerecipeDestroyMutation,
  useUsersUserfavouriterecipeListQuery,
  useUsersUserdislikerecipeListQuery,
  useFoodIngredientPartialUpdateMutation,
  useFoodRecipeMealRetrieveQuery,
  useFoodIngredientCreateMutation,
  useFoodIngredientDestroyMutation,
  useFoodRecipeMealPartialUpdateMutation,
  useFoodRecipeTemplatePartialUpdateMutation,
  usePlannerEditRecipeMealPortionsUpdateMutation,
} = backendApi;

type MealForEditScreenBase = GeneratedRecipeMeal | RecipeMeal;
type MealForEditScreen = MealForEditScreenBase & {
  portions_to_plan?: number;
};

type Props = NativeStackScreenProps<RootStackParamList, Routes.RecipeDetailScreen>;
const RecipeDetailScreen = ({
  navigation,
  route: {
    params: {
      recipeMeal,
      mealSlotSpecification,
      onSelect,
      viewOnly = false,
      editable = false,
      recipeTemplateEditable = false,
      portionsEditable = false,
    },
  },
}: Props): JSX.Element => {
  const { t } = useTranslation();
  // TODO: This is commented out because it is not implemented yet
  // const [searchText, setSearchText] = useState("");
  // const [, setSelectedCategory] = useState();
  // const [, setModalVisible] = useState(false);

  const theme = useTheme();

  const dispatch = useDispatch();
  // TODO: This should be removed to avoid potential errors but it is working at the moment
  const viewAsUser = useSelector(viewAsUserSelector);
  const realUser = useSelector(userSelector);
  const user = viewAsUser || realUser;

  const favouritedRecipes = useSelector(userFavouriteRecipesSelector);
  const dislikedRecipes = useSelector(userDislikeRecipesSelector);

  const layout = useWindowDimensions();
  const isDesktop = isDesktopScreen();
  const recipeTemplate = recipeMeal.recipe_template;

  let userFavouriteRecipe: UserFavouriteRecipe | undefined;
  let userDislikeRecipe: UserFavouriteRecipe | undefined;

  if (!recipeTemplate.id) {
    logger.error("Recipe template id is missing for recipeMeal:", recipeMeal);
  } else {
    userFavouriteRecipe = getFavouritedRecipe(favouritedRecipes, recipeTemplate.id);

    userDislikeRecipe = getDislikedRecipe(dislikedRecipes || [], recipeTemplate.id);
  }

  // eslint-disable-next-line no-prototype-builtins
  if (editable && !recipeMeal.hasOwnProperty("id")) {
    throw new Error("Tried to display not editable recipe details in edit mode");
  }

  const [index, setIndex] = useState(0);

  if (!user) {
    throw new Error("user is undefined");
  }

  const {
    isFetching: isLoadingUserFavoriteRecipe,
    data: paginatedUserFavouriteRecipeList,
    error: errorUserFavouriteRecipeList,
    refetch: refetchUserFavouriteRecipeList,
  } = useUsersUserfavouriterecipeListQuery({
    user: user.id,
    recipe: recipeTemplate.id,
  });

  const {
    isFetching: isLoadingUserDislikeRecipe,
    data: paginatedUserDislikeRecipeList,
    error: errorUserDislikeRecipeList,
    refetch: refetchUserDislikeRecipeList,
  } = useUsersUserdislikerecipeListQuery({
    user: user.id,
    recipe: recipeTemplate.id,
  });

  const [deleteUserFavouriteRecipeOnBackend, { isLoading: isLoadingFavDelete }] =
    useUsersUserfavouriterecipeDestroyMutation();
  const [createNewUserFavouriteRecipe, { isLoading: isLoadingFavCreate }] = useUsersUserfavouriterecipeCreateMutation();
  const [deleteUserDislikeRecipeOnBackend, { isLoading: isLoadingDislikeDelete }] =
    useUsersUserdislikerecipeDestroyMutation();
  const [createNewUserDislikeRecipe, { isLoading: isLoadingDislikeCreate }] = useUsersUserdislikerecipeCreateMutation();

  const [updateRecipeMealIngredient, { isLoading: isLoadingIngredientUpdate }] =
    useFoodIngredientPartialUpdateMutation();
  const [updateRecipeMeal, { isLoading: isLoadingRecipeMealUpdate }] = useFoodRecipeMealPartialUpdateMutation();
  const { refetchCalendarDays } = getPlannerData();
  const [createIngredient, { isLoading: isLoadingIngredientCreate }] = useFoodIngredientCreateMutation();
  const [deleteIngredient, { isLoading: isLoadingIngredientDelete }] = useFoodIngredientDestroyMutation();
  const [updateRecipeTemplate, { isLoading: isLoadingRecipeTemplateUpdate }] =
    useFoodRecipeTemplatePartialUpdateMutation();
  const [updateRecipeMealPortions, { isLoading: isLoadingUpdateRecipeMealPortions }] =
    usePlannerEditRecipeMealPortionsUpdateMutation();

  // TODO: rename
  const [bufferedRecipeMeal, setBufferedRecipeMeal] = useState<MealForEditScreen>(recipeMeal);

  const isAuserGeneratedMeal = bufferedRecipeMeal.recipe_template.source_provider === "USER_GENERATED";

  const DUTCH_MIGRATED_LEGACY_MEAL_DIRECTION = "Zelfgemaakte maaltijd";
  const ENGLISH_MIGRATED_LEGACY_MEAL_DIRECTION = "Self-created meal";

  const isMigratedLegacyMeal =
    _.size(bufferedRecipeMeal.directions) === 1 &&
    [DUTCH_MIGRATED_LEGACY_MEAL_DIRECTION, ENGLISH_MIGRATED_LEGACY_MEAL_DIRECTION].includes(
      bufferedRecipeMeal.directions?.[0]?.text || ""
    );

  const hasDirections = !_.isEmpty(bufferedRecipeMeal.directions);

  const shouldShowPreparationSteps = hasDirections && !isMigratedLegacyMeal;

  const {
    isFetching: isLoadingRecipeMealQuery,
    error: errorRetrievingRecipeMeal,
    data: recipeMealOnBackend,
    refetch: refetchRecipeMeal,
  } = useFoodRecipeMealRetrieveQuery(
    // TODO: How can we legitimately define a hook when bufferedRecipeMeal may not
    // have an id
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    { id: bufferedRecipeMeal.id },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    { skip: !bufferedRecipeMeal.id }
  );

  const shouldShowLoadingSpinner =
    isLoadingFavDelete ||
    isLoadingFavCreate ||
    isLoadingDislikeDelete ||
    isLoadingDislikeCreate ||
    isLoadingIngredientUpdate ||
    isLoadingRecipeMealUpdate ||
    isLoadingIngredientCreate ||
    isLoadingIngredientDelete ||
    isLoadingRecipeMealQuery ||
    isLoadingRecipeTemplateUpdate ||
    isLoadingUserFavoriteRecipe ||
    isLoadingUserDislikeRecipe ||
    isLoadingUpdateRecipeMealPortions;

  useEffect(() => {
    if (recipeMealOnBackend && recipeMealOnBackend.id) {
      setBufferedRecipeMeal(recipeMealOnBackend);
    }
  }, [recipeMealOnBackend]);

  useEffect(() => {
    if (!isLoadingUserFavoriteRecipe && !errorUserFavouriteRecipeList && recipeTemplate.id) {
      if (
        paginatedUserFavouriteRecipeList &&
        paginatedUserFavouriteRecipeList.results &&
        paginatedUserFavouriteRecipeList.results[0]
      ) {
        dispatch(
          userSlice.actions.storeUserFavouriteRecipe({
            [recipeTemplate.id]: paginatedUserFavouriteRecipeList.results[0],
          })
        );
      } else {
        dispatch(userSlice.actions.deleteUserFavouriteRecipe({ recipeTemplateId: recipeTemplate.id }));
      }
    }
  }, [paginatedUserFavouriteRecipeList]);

  useEffect(() => {
    if (!isLoadingUserDislikeRecipe && !errorUserDislikeRecipeList && recipeTemplate.id) {
      if (
        paginatedUserDislikeRecipeList &&
        paginatedUserDislikeRecipeList.results &&
        paginatedUserDislikeRecipeList.results[0]
      ) {
        dispatch(
          userSlice.actions.storeUserDislikeRecipe({
            [recipeTemplate.id]: paginatedUserDislikeRecipeList.results[0],
          })
        );
      } else {
        dispatch(userSlice.actions.deleteUserDislikeRecipe({ recipeTemplateId: recipeTemplate.id }));
      }
    }
  }, [paginatedUserDislikeRecipeList]);

  const refreshRecipeMealData = async (): Promise<void> => {
    // NOTE: refetching calendar data is an expensive operation to do for simply updating a recipeMeal,
    // but we do it for now and can rethink this later if there is a performance issue
    // With cache invalidation configured, the calendar data will auto-refresh when ingredients change
    await refetchRecipeMeal();
    refetchCalendarDays(); // Called without await since cache invalidation handles this
  };

  const onEditPortions = async (portions: number): Promise<void> => {
    if (!(bufferedRecipeMeal as RecipeMeal).id || !portionsEditable) {
      setBufferedRecipeMeal({ ...bufferedRecipeMeal, portions_to_plan: portions });
      return;
    }

    await updateRecipeMealPortions({
      id: String((bufferedRecipeMeal as RecipeMeal).id),
      recipeMealPotionsRequest: {
        portions,
      },
    });

    await refreshRecipeMealData();
  };

  const onEditIngredientQuantity = async (ingredient: Ingredient, quantity: number): Promise<void> => {
    if (!ingredient.id || !editable) {
      throw new Error("Tried to edit a non editable Ingredient or GeneratedRecipeMealIngredient");
    }

    if (isLoadingRecipeMealQuery) {
      logger.warn("Tried to edit an ingredient quantity while recipeMeal is still loading");
      return;
    }

    if (!quantity) {
      await deleteIngredient({ id: ingredient.id });
    } else {
      await updateRecipeMealIngredient({
        id: ingredient.id,
        patchedIngredientRequest: {
          quantity: Math.round(quantity * 20) / 20,
        },
      });
    }

    await refreshRecipeMealData();
  };

  const onFavouriteButtonPress = async (): Promise<void> => {
    if (!isLoadingUserFavoriteRecipe) {
      if (recipeTemplate.id) {
        if (userFavouriteRecipe) {
          await deleteUserFavouriteRecipeOnBackend({ id: userFavouriteRecipe.id }).unwrap();
        } else {
          await createNewUserFavouriteRecipe({
            userFavouriteRecipeRequest: { recipe: recipeTemplate.id, user: user.id },
          }).unwrap();
        }
        refetchUserFavouriteRecipeList();
      }
    }
  };

  const onDislikeButtonPress = async (): Promise<void> => {
    if (!isLoadingUserDislikeRecipe) {
      if (recipeTemplate.id) {
        if (userDislikeRecipe) {
          await deleteUserDislikeRecipeOnBackend({ id: userDislikeRecipe.id }).unwrap();
        } else {
          await createNewUserDislikeRecipe({
            userDislikeRecipeRequest: { recipe: recipeTemplate.id, user: user.id },
          }).unwrap();
        }
        refetchUserDislikeRecipeList();
      }
    }
  };

  const onCloseBtn = (): void => {
    navigation.goBack();
  };

  const onChooseProduct = async (
    ingredient: Ingredient | undefined,
    product: Food,
    suggestedServing: SuggestedServing,
    quantity: number
  ): Promise<void> => {
    if (!(bufferedRecipeMeal as RecipeMeal).id || !editable) {
      throw new Error("Tried to edit a non editable RecipeMeal or GeneratedRecipeMeal");
    }

    if (!suggestedServing.id) {
      throw new Error("Suggested serving is not set");
    }

    const ingredientPostRequest: IngredientPostRequest = {
      quantity,
      object_id: (bufferedRecipeMeal as RecipeMeal).id,
      content_type: "recipemeal",
      suggested_serving: suggestedServing.id,
    };

    if (ingredient && ingredient.id) {
      // Update the ingredient
      await updateRecipeMealIngredient({
        id: ingredient.id,
        patchedIngredientRequest: {
          quantity,
          suggested_serving: suggestedServing,
        },
      });
    } else {
      await createIngredient({ ingredientPostRequest });
    }
    // Navigate immediately - cache invalidation will auto-refresh the data
    navigation.goBack();
  };

  const handleAddIngredient = (): void => {
    navigation.push(Routes.AddProductStack, {
      screen: Routes.FoodSearchScreen,
      params: {
        onChoose: async (product, suggestedServing, quantity) =>
          onChooseProduct(undefined, product, suggestedServing, quantity),
      },
    });
  };

  const handleEditIngredient = (ingredient: Ingredient): void => {
    if (!editable || !ingredient) {
      throw new Error("Tried to edit a non editable Ingredient or GeneratedRecipeMealIngredient");
    }

    navigation.push(Routes.AddProductStack, {
      screen: Routes.AddIngredientScreen,
      params: {
        ingredient,
        onChoose: async (product, suggestedServing, quantity) =>
          onChooseProduct(ingredient, product, suggestedServing, quantity),
      },
    });
  };

  const ingredientsListRoute = (
    <MemoizedIngredientsRoute
      {...{
        editable,
        portionsEditable,
        bufferedRecipeMeal,
        onEditPortions,
        onEditIngredientQuantity,
        handleAddIngredient,
        handleEditIngredient,
        showLoading: isLoadingIngredientDelete || isLoadingIngredientUpdate,
        t,
      }}
    />
  );

  const isAUserGeneratedMeal = bufferedRecipeMeal.recipe_template.source_provider === "USER_GENERATED";

  const createMacroItem = (macroName: MacroName): RecipeMacrosItemType => {
    let progress = bufferedRecipeMeal[macroName];

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (isAUserGeneratedMeal && !bufferedRecipeMeal.id) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      progress = bufferedRecipeMeal.recipe_template[macroName] || 0;
    }

    return {
      id: macroName,
      macroName,
      total: mealSlotSpecification[macroName] ?? 0,
      progress,
      unit: macroName === "kcal" ? "kcal" : "g",
    };
  };
  const nutritionalInformationForRecipe = _.map(MACRO_NAMES, createMacroItem);

  const [submitted, setSubmitted] = useState(false);

  const onEditRecipeTemplate = (): void => {
    navigation.push(Routes.RecipeAddOwnStack, {
      screen: Routes.RecipeAddOwnScreen,
      params: {
        mealSlotSpecification,
        initialRecipeMeal: bufferedRecipeMeal,
        onSave: async () => {
          // Unpublish initial recipe template
          // TODO: while we should have this guard is there a better way to
          //  prevent WEEKMEALS recipe templates being unpublished? Could we enforce that better?
          if (
            bufferedRecipeMeal.recipe_template.source_provider === "USER_GENERATED" &&
            bufferedRecipeMeal.recipe_template.id
          ) {
            await updateRecipeTemplate({
              id: bufferedRecipeMeal.recipe_template.id,
              patchedRecipeTemplateRequest: {
                published: false,
              },
            }).unwrap();
          }
        },
      },
    });
  };
  const isDefaultImg = getRecipeMealImage(recipeMeal).usedDefaultImage;
  const imageStyle = isDefaultImg ? styles.defaultImgContainer : styles.imgContainer;

  const recipeImageComponent = React.useCallback(
    () => (
      <Box
        width="100%"
        height={isDesktop ? "400px" : width}
        position="relative"
        justifyContent="center"
        alignItems="center"
        bg="gray.50"
      >
        <Image
          source={getRecipeMealImage(recipeMeal).source}
          resizeMode={getRecipeMealImage(recipeMeal).usedDefaultImage ? "contain" : "cover"}
          width="100%"
          height="100%"
          alt={t("recipe_detail.recipe_image_alt")}
        />

        <Box position="absolute" top={`${VerticalScale(40)}px`} right={`${Scale(16)}px`} zIndex={1000}>
          <Pressable onPress={onCloseBtn} testID={"closeRecipeDetailScreen-button"}>
            <Box
              bg="white"
              borderRadius="full"
              width={12}
              height={12}
              justifyContent="center"
              alignItems="center"
              shadow={2}
            >
              <Icon as={MaterialIcons} name="close" size="lg" color="gray.700" />
            </Box>
          </Pressable>
        </Box>

        {/* Action buttons */}
        <HStack position="absolute" bottom={`${VerticalScale(16)}px`} right={`${Scale(16)}px`} space={3} zIndex={1000}>
          <Pressable
            onPress={(): void => {
              onDislikeButtonPress().catch(() => {
                logger.error("Failed to dislike recipe");
                throw new Error("Failed to dislike recipe");
              });
            }}
            disabled={isLoadingUserDislikeRecipe || isLoadingDislikeDelete}
            testID={`dislike-button-${bufferedRecipeMeal.recipe_template.id || "MISSING_ID"}`}
          >
            <Box
              bg="white"
              borderRadius="full"
              width={12}
              height={12}
              justifyContent="center"
              alignItems="center"
              shadow={2}
            >
              <Icon
                as={MaterialIcons}
                name={userDislikeRecipe ? "thumb-down" : "thumb-down-off-alt"}
                size="md"
                color={userDislikeRecipe ? "blue.500" : "gray.500"}
              />
            </Box>
          </Pressable>

          <Pressable
            onPress={(): void => {
              onFavouriteButtonPress().catch(() => {
                logger.error("Failed to favourite recipe");
                throw new Error("Failed to favourite recipe");
              });
            }}
            disabled={isLoadingUserFavoriteRecipe || isLoadingFavDelete}
            testID={`favourite-button-${bufferedRecipeMeal.recipe_template.id || "MISSING_ID"}`}
          >
            <Box
              bg="white"
              borderRadius="full"
              width={12}
              height={12}
              justifyContent="center"
              alignItems="center"
              shadow={2}
            >
              <Icon
                as={MaterialIcons}
                name={userFavouriteRecipe ? "favorite" : "favorite-border"}
                size="md"
                color={userFavouriteRecipe ? "red.500" : "gray.500"}
              />
            </Box>
          </Pressable>
        </HStack>
      </Box>
    ),
    [
      bufferedRecipeMeal.recipe_template.id,
      isDesktop,
      isLoadingFavDelete,
      isLoadingUserFavoriteRecipe,
      userFavouriteRecipe,
      isLoadingDislikeDelete,
      isLoadingUserDislikeRecipe,
      userDislikeRecipe,
    ]
  );

  const handleInstagramIconPress = (url: string): void => {
    openURL(url);
  };

  const { isLoading: isLoadingExternalLink, error: externalLinkError, openLink } = useExternalLink();

  const shouldShowHormoneRecipeInfo =
    bufferedRecipeMeal?.recipe_template.tags?.some((tag) => tag.name === "HORMONE") &&
    isFeatureFlagActive(user, FeatureFlag.FF_FEMALE_HORMONES);

  const recipeInformationComponent = (
    <VStack space={6} px={4} py={6} bg="white">
      {/* Title and Edit Section */}
      <HStack justifyContent="space-between" alignItems="flex-start">
        <VStack flex={1} space={2}>
          <Text fontSize="2xl" fontWeight="600" color="gray.800" lineHeight="28px">
            {bufferedRecipeMeal?.recipe_template.name}
          </Text>

          {Boolean(bufferedRecipeMeal?.recipe_template?.preparation_time_min) && (
            <Text fontSize="sm" color="gray.500" fontWeight="500">
              {bufferedRecipeMeal?.recipe_template.preparation_time_min} mins
            </Text>
          )}
        </VStack>

        {recipeTemplateEditable ? (
          <Pressable onPress={onEditRecipeTemplate} testID={"editRecipeTemplateRecipeDetailScreen-button"}>
            <Box
              bg="gray.100"
              borderRadius={theme.borderRadii.card.default}
              width={12}
              height={12}
              justifyContent="center"
              alignItems="center"
            >
              <Icon as={MaterialIcons} name="edit" size={Scale(18)} color="gray.600" />
            </Box>
          </Pressable>
        ) : null}
      </HStack>

      {/* Hormone recipe information */}
      {shouldShowHormoneRecipeInfo ? (
        <Box
          bg={`${HORMONE_HEX_COLOR}15`}
          px={4}
          py={3}
          borderRadius="8px"
          borderLeftWidth={4}
          borderLeftColor={HORMONE_HEX_COLOR}
        >
          <HStack alignItems="flex-start" space={3}>
            <Icon as={Ionicons} name="female" size="md" color={HORMONE_HEX_COLOR} mt={0.5} />
            <VStack flex={1} space={1}>
              <Text color="gray.700" fontWeight="500" fontSize="sm">
                {t("recipe_detail.hormone_recipe_info")}
              </Text>
            </VStack>
          </HStack>
        </Box>
      ) : null}

      {/* Instagram Link */}
      {isInstagramExternalSource(bufferedRecipeMeal?.recipe_template?.external_url || "") ? (
        <Pressable
          onPress={(): void => handleInstagramIconPress(bufferedRecipeMeal?.recipe_template?.external_url || "")}
          testID="instagramButton"
        >
          <HStack alignItems="center" space={2}>
            <Icon as={Ionicons} name="logo-instagram" size="md" color="red.500" />
            <Text color="red.500" fontWeight="500" fontSize="sm">
              {t("recipe_detail.view_on_instagram")}
            </Text>
          </HStack>
        </Pressable>
      ) : null}

      {/* Nutrition Information */}
      <VStack space={4}>
        <VStack space={4}>
          <HStack justifyContent="space-between">
            {/* Calories */}
            <VStack alignItems="flex-start" flex={1}>
              <Text fontSize="xl" fontWeight="600" color="gray.800">
                {formatNumberAsWholeNumber(nutritionalInformationForRecipe[0]?.progress || 0)}
              </Text>
              <Text fontSize="sm" color="gray.600" fontWeight="500">
                {t("general.calories")}
              </Text>
            </VStack>

            {/* Protein */}
            <VStack alignItems="flex-start" flex={1}>
              <Text fontSize="xl" fontWeight="600" color="gray.800">
                {formatNumberAsWholeNumber(nutritionalInformationForRecipe[1]?.progress || 0)}{" "}
                {nutritionalInformationForRecipe[1]?.unit}
              </Text>
              <Text fontSize="sm" color="gray.600" fontWeight="500">
                {t("general.protein")}
              </Text>
            </VStack>
          </HStack>

          <HStack justifyContent="space-between">
            {/* Carbohydrates */}
            <VStack alignItems="flex-start" flex={1}>
              <Text fontSize="xl" fontWeight="600" color="gray.800">
                {formatNumberAsWholeNumber(nutritionalInformationForRecipe[2]?.progress || 0)}{" "}
                {nutritionalInformationForRecipe[2]?.unit}
              </Text>
              <Text fontSize="sm" color="gray.600" fontWeight="500">
                {t("general.carbohydrates")}
              </Text>
            </VStack>

            {/* Fat */}
            <VStack alignItems="flex-start" flex={1}>
              <Text fontSize="xl" fontWeight="600" color="gray.800">
                {formatNumberAsWholeNumber(nutritionalInformationForRecipe[3]?.progress || 0)}{" "}
                {nutritionalInformationForRecipe[3]?.unit}
              </Text>
              <Text fontSize="sm" color="gray.600" fontWeight="500">
                {t("general.fat")}
              </Text>
            </VStack>
          </HStack>
        </VStack>
      </VStack>
    </VStack>
  );

  const ingredientsAndDirectionsTabsComponent = (
    <VStack space={8} px={4} py={6} bg="white">
      {/* Ingredients Section */}
      <VStack space={4}>
        <Box>{ingredientsListRoute}</Box>
      </VStack>

      {/* Preparation Steps Section */}
      {shouldShowPreparationSteps ? (
        <VStack space={4}>
          <Box>
            <PreparationStepsRoute bufferedRecipeMeal={bufferedRecipeMeal} t={t} />
          </Box>
        </VStack>
      ) : null}
    </VStack>
  );

  const chooseMealComponent = (
    <Box px={4} py={6} bg="white" borderTopWidth={1} borderTopColor="gray.200">
      <Button
        onPress={(): void => {
          setSubmitted(true);
          onSelect(bufferedRecipeMeal.portions_to_plan || 1).catch(() => {
            logger.error("Failed to select recipe");
            throw new Error("Failed to select recipe");
          });
        }}
        isLoading={submitted}
        testID="choose-meal"
        bg="primary.500"
        borderRadius={theme.borderRadii.button.default}
        height={12}
        _text={{
          fontSize: "md",
          fontWeight: "600",
          color: "white",
        }}
        _pressed={{
          bg: "primary.600",
        }}
      >
        {t("recipe_search.choose_for_meal_button_text", {
          meal: formatMealType(mealSlotSpecification.meal_type, t),
        })}
      </Button>
    </Box>
  );

  return (
    <Box flex={1} bg="white">
      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ flexGrow: 1 }}>
        {recipeImageComponent()}
        {recipeInformationComponent}
        {ingredientsAndDirectionsTabsComponent}
      </ScrollView>

      {!viewOnly && chooseMealComponent}

      {/* Loading overlay */}
      {shouldShowLoadingSpinner ? (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="white"
          opacity={0.8}
          justifyContent="center"
          alignItems="center"
          zIndex={9999}
        >
          <Spinner size="lg" color="green.500" />
        </Box>
      ) : null}
    </Box>
  );
};
export default RecipeDetailScreen;
